/* eslint-disable react-hooks/rules-of-hooks */
import { h, Component } from 'preact';
import { Text, useText } from 'preact-i18n';
import Cookies from 'js-cookie';
import { Form, Validators, validateField } from '../utils/form-helpers';
import modalContent from '../constants/modalContent';
import SignInWith from './SignInWith';
import FieldWrapper from './generics/FieldWrapper';
import ButtonWrapper from './generics/ButtonWrapper';
import AlertMessage from './generics/AlertMessage';
import { removeAllURLParams } from '../utils/urlsCleaner';
import psl from 'psl';
import { getPubSubEventName } from '../utils/pubSub';
import { executeRecaptcha } from '../utils/googleRecaptcha';
import withConfig from './hoc/withContext';

const EMAIL_FIELD = 'email';
const PASSWORD_FIELD = 'password';
const BRAND_FIELD = 'brand';

class SignInContent extends Component {
  constructor(props) {
    super(props);
    this.state = {
      recaptchaToken: undefined,
      error: '',
      isLoading: false,
      form: new Form({
        [PASSWORD_FIELD]: {
          value: '',
          validators: [Validators.required()],
        },
        [EMAIL_FIELD]: {
          value: '',
          validators: [Validators.required(), Validators.email()],
        },
        [BRAND_FIELD]: {
          value: '',
          validators: [
            this.props.widgetProps.availableWallets
              ? Validators.required()
              : null,
          ].filter(Boolean),
        },
      }),
    };

    this.onSubmit = this.onSubmit.bind(this);
    this.onCaptchaVerify = this.onCaptchaVerify.bind(this);
  }

  componentDidMount() {
    const {
      config: { reCaptchaKey },
      widgetProps,
    } = this.props;
    const { form } = this.state;
    form.clearValues(this);

    const { captchaEnabled } = widgetProps;
    // Retrieve captcha token
    if (captchaEnabled && reCaptchaKey) {
      this.onCaptchaVerify();
    }
  }

  onCaptchaVerify() {
    executeRecaptcha('siempre_plus_login', recaptchaToken => {
      this.setState({ recaptchaToken });
    });
  }

  onSubmit() {
    const { form, recaptchaToken } = this.state;
    const {
      session,
      mixpanelTracker,
      widgetProps,
      config: { tokenName },
    } = this.props;
    const { cardType } = widgetProps;
    const { pub } = window.reservamosPubSub;
    const { profile: profileEventName } = getPubSubEventName(cardType);
    this.setState({
      isLoading: true,
      error: '',
    });
    mixpanelTracker.trackSignInClick(null);
    session
      .signIn(
        {
          email: form.getValue(EMAIL_FIELD),
          password: form.getValue(PASSWORD_FIELD),
          brand: form.getValue(BRAND_FIELD),
          recaptchaToken,
        },
        widgetProps.cardType,
      )
      .then(user => {
        if (cardType === 'siempreplus') {
          mixpanelTracker.trackSignIn({
            [`${widgetProps.mixpanelPrefix} Card`]: user.sp_card,
            Type: `${widgetProps.mixpanelPrefix} Card`,
          });
        }

        const domain = psl.parse(window.location.hostname).domain;
        Cookies.set(tokenName, user.token, {
          domain,
          expires: widgetProps.cookieExpiration,
        });

        this.setState({ isLoading: false });
        pub(profileEventName, {
          user,
          loyaltyName: cardType,
          loading: false,
          error: null,
        });
      })
      .catch(({ response }) => {
        this.setState({ isLoading: false });

        // If recaptcha is enabled and token is set, we need to verify and set it again
        if (recaptchaToken) {
          this.onCaptchaVerify();
        }

        response
          .json()
          .then(({ errors: [error] }) => {
            pub(profileEventName, {
              loading: false,
              user: null,
              loyaltyName: cardType,
              error,
            });
            this.setState({ error: error.message });
          })
          .catch(error => {
            pub(profileEventName, {
              loading: false,
              user: null,
              loyaltyName: cardType,
              error,
            });
            this.setState({ error: 'unexpected_error' });
          });
      })
      .finally(() => {
        history.replaceState(
          {},
          null,
          removeAllURLParams(window.location.href),
        );
      });
  }

  renderWalletSelection(brandsArray, hasMultipleWallets, form) {
    const { onBrandChange } = this.props;
    const numberOfBrands = brandsArray ? brandsArray.length : 0;
    // Set the default brand if not already set
    if (!form.getValue(BRAND_FIELD) && numberOfBrands > 0) {
      form.setValue(BRAND_FIELD, brandsArray[0].brand);
      // Notify parent of default brand selection
      if (onBrandChange) {
        onBrandChange(brandsArray[0].brand);
      }
    }

    // This is necessary to bypass rendering the selector while still allowing form data submission
    if (!hasMultipleWallets) return;

    const containerStyle = {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: '10px',
      marginBottom: '10px',
      width: '100%',
      overflow: 'hidden',
    };

    const brandStyle = {
      margin: '10px',
      flex: '0 1 150px',
      textAlign: 'center',
    };

    const inputStyle = {
      display: 'none',
    };

    const getButtonStyle = isSelected => ({
      border: '2px solid',
      borderColor: isSelected ? '#315793' : '#ccc',
      background: isSelected ? '#d0e1f9' : 'transparent',
      cursor: 'pointer',
      borderRadius: '8px',
      boxShadow: isSelected ? '0 0 10px rgba(0, 0, 0, 0.2)' : 'none',
      width: '150px',
      height: '100px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '10px',
      transition: 'all 0.3s ease',
      textAlign: 'center',
    });

    const handleBrandChange = event => {
      validateField(this, form)(event);
      // Notify parent of brand change
      if (onBrandChange) {
        onBrandChange(event.target.value);
      }
    };

    const renderBrandOption = brand => (
      <div key={brand.id} style={brandStyle}>
        <label>
          <input
            type="radio"
            name={BRAND_FIELD}
            value={brand.brand}
            style={inputStyle}
            onChange={handleBrandChange}
            checked={form.getValue(BRAND_FIELD) === brand.brand}
          />
          <div
            style={getButtonStyle(form.getValue(BRAND_FIELD) === brand.brand)}
          >
            {this.renderLogo(brand)}
          </div>
        </label>
      </div>
    );

    return (
      <div style={containerStyle}>
        <FieldWrapper
          hasError={form.fieldHasErrors(BRAND_FIELD)}
          className="form-item"
          errorLegend="invalid_brand"
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {brandsArray.map(renderBrandOption)}
          </div>
        </FieldWrapper>
      </div>
    );
  }

  renderLogo(brand) {
    const logoStyle = { width: '90px', height: '36px' };

    return <img src={brand.logoUrl} alt={brand.name} style={logoStyle} />;
  }

  render({ onChangeContent, mixpanelTracker, thirdParty }) {
    const { form, isLoading, error } = this.state;
    const { widgetProps } = this.props;
    const {
      recoverPassword,
      registerAccount,
      facebookLogin,
      googleLogin,
      outlookLogin,
      externalRegisterUrl,
      availableWallets,
    } = widgetProps;

    const brandsArray = (() => {
      try {
        return JSON.parse(availableWallets) || [];
      } catch (error) {
        return [];
      }
    })();
    const hasMultipleWallets = brandsArray.length > 1;

    const hideForgotLink = Boolean(recoverPassword) === false;
    const hideRegisterLink = Boolean(registerAccount) === false;
    const { placeholder_email, placeholder_password, select_wallet } = useText({
      placeholder_email: 'placeholder.email',
      placeholder_password: 'placeholder.password',
      select_wallet: 'label.select_wallet',
    });

    function findBrandHref() {
      const selectedBrand = form.getValue(BRAND_FIELD);
      const brand = brandsArray.find(({ brand }) => brand === selectedBrand);
      return brand?.urls?.register || null;
    }

    let registerButtonActionProps = {};

    if (externalRegisterUrl) {
      registerButtonActionProps.href = externalRegisterUrl;
    } else if (brandsArray && brandsArray?.length > 0) {
      registerButtonActionProps.href = findBrandHref();
    } else {
      registerButtonActionProps.onClick = () =>
        onChangeContent(modalContent.SIGN_UP);
    }

    const signInWithCallback = type =>
      mixpanelTracker.trackSignIn({
        [`${widgetProps.mixpanelPrefix} Card`]: null,
        Type: type,
      });
    return (
      <div>
        <form onSubmit={form.onSubmit(this, this.onSubmit)}>
          <AlertMessage type="error" visible={Boolean(error)}>
            {error}
          </AlertMessage>

          <div>
            {hasMultipleWallets && <h1>{select_wallet}</h1>}
            {this.renderWalletSelection(
              brandsArray,
              hasMultipleWallets,
              form,
              select_wallet,
            )}
          </div>
          <FieldWrapper
            hasError={form.fieldHasErrors(EMAIL_FIELD)}
            className="form-item"
            errorLegend="invalid_email"
          >
            <input
              type="text"
              placeholder={placeholder_email}
              name={EMAIL_FIELD}
              onInput={validateField(this, form)}
            />
          </FieldWrapper>
          <FieldWrapper
            hasError={form.fieldHasErrors(PASSWORD_FIELD)}
            className="form-item"
            errorLegend="required_field"
          >
            <input
              type="password"
              placeholder={placeholder_password}
              name={PASSWORD_FIELD}
              onInput={validateField(this, form)}
            />
          </FieldWrapper>
          {hideForgotLink ? null : (
            <div className="form-item">
              <p className="w-modal-copy forgot-link">
                {/* eslint-disable-next-line */}
                <a
                  className="w-modal-link"
                  onClick={() => onChangeContent(modalContent.FORGOT_PIN)}
                >
                  <Text id="link.did_you_forget_your_password" />
                </a>
              </p>
            </div>
          )}
          <div className="form-item">
            <ButtonWrapper
              className="w-modal-button"
              type="submit"
              isLoading={isLoading}
            >
              <Text id={'button.log_in'} />
            </ButtonWrapper>
          </div>
        </form>
        {thirdParty ? (
          <SignInWith
            facebookLogin={facebookLogin}
            googleLogin={googleLogin}
            outlookLogin={outlookLogin}
            onSelect={signInWithCallback}
          />
        ) : null}
        {hideRegisterLink
          ? null
          : [
              // eslint-disable-next-line react/jsx-key
              <div className="or">
                <hr className="bar" />
                <span>O</span>
                <hr className="bar" />
              </div>,
              // eslint-disable-next-line react/jsx-key
              <div>
                <p className="w-modal-copy account-link">
                  {/* eslint-disable-next-line */}
                  <a className="w-modal-link" {...registerButtonActionProps}>
                    <Text id="link.you_do_not_have_an_account" />
                    <span>
                      <Text id="link.sign_up" />
                    </span>
                  </a>
                </p>
              </div>,
            ]}
      </div>
    );
  }
}

export default withConfig(SignInContent);
